import Cookies from 'js-cookie';

const TOKEN_COOKIE = 'usertoken';
const TOKEN_STORAGE = 'dalipen_auth_token';
const USER_STORAGE = 'dalipen_remembered_user';
const SESSION_DAYS = 7;
let currentToken = '';

export interface RememberedUser {
  email: string;
  username: string;
}

const canUseStorage = () => typeof window !== 'undefined' && Boolean(window.localStorage);

const cookieOptions = () => ({
  expires: SESSION_DAYS,
  path: '/',
  sameSite: 'lax' as const,
  secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
});

export const getAuthToken = () => {
  if (currentToken) {
    return currentToken;
  }

  const storedToken = canUseStorage() ? window.localStorage.getItem(TOKEN_STORAGE) || '' : '';
  if (storedToken) {
    currentToken = storedToken;
    Cookies.set(TOKEN_COOKIE, storedToken, cookieOptions());
    return storedToken;
  }

  const cookieToken = Cookies.get(TOKEN_COOKIE) || '';
  if (cookieToken) {
    currentToken = cookieToken;
    if (canUseStorage()) {
      window.localStorage.setItem(TOKEN_STORAGE, cookieToken);
    }
  }
  return cookieToken;
};

export const setAuthToken = (token: string) => {
  if (typeof token !== 'string' || token.length === 0) {
    return;
  }

  // The API compares this entire opaque string, including every delimiter.
  currentToken = token;
  if (canUseStorage()) {
    window.localStorage.setItem(TOKEN_STORAGE, token);
  }
  Cookies.remove(TOKEN_COOKIE, { path: '/' });
  Cookies.remove(TOKEN_COOKIE, { path: '/', domain: 'dalipen.com' });
  Cookies.remove(TOKEN_COOKIE, { path: '/', domain: '.dalipen.com' });
  Cookies.set(TOKEN_COOKIE, token, cookieOptions());
};

export const clearAuthToken = () => {
  currentToken = '';
  Cookies.remove(TOKEN_COOKIE, { path: '/' });
  Cookies.remove(TOKEN_COOKIE, { path: '/', domain: 'dalipen.com' });
  Cookies.remove(TOKEN_COOKIE, { path: '/', domain: '.dalipen.com' });
  if (canUseStorage()) {
    window.localStorage.removeItem(TOKEN_STORAGE);
  }
};

export const getRememberedUser = (): RememberedUser => {
  if (!canUseStorage()) {
    return { email: '', username: '' };
  }
  try {
    const value = JSON.parse(window.localStorage.getItem(USER_STORAGE) || '{}');
    return {
      email: typeof value.email === 'string' ? value.email : '',
      username: typeof value.username === 'string' ? value.username : '',
    };
  } catch (error) {
    return { email: '', username: '' };
  }
};

export const rememberUser = (email: string, username = '') => {
  if (!email || !canUseStorage()) {
    return;
  }
  const nextUsername = username.trim() || email.replace(/@.*/, '');
  window.localStorage.setItem(USER_STORAGE, JSON.stringify({
    email,
    username: nextUsername,
  }));
};

export const hasAuthToken = () => Boolean(getAuthToken());
